import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { SEGMENT_COLORS, getSegmentDisplayName } from "../constants/segments";
import { formatCompactCurrency, formatCurrency, formatNumber } from "../utils/formatters";
import { SegmentCrest, renderHighlightedInsight } from "./SegmentSummary";

const SEGMENT_ORDER = [
  "Champions",
  "Loyal Customers",
  "Promising",
  "Need Attention",
  "At Risk",
  "Lost",
];
const MIN_TREEMAP_LABEL_SHARE = 4;
const DEFAULT_TREEMAP_CHART_HEIGHT = 540;
const APP_FONT_FAMILY = '"Avenir Next", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const SEGMENT_TINT_COLORS = {
  Champions: "rgb(194, 215, 250)",
  "Loyal Customers": "rgb(191, 229, 216)",
  Promising: "rgb(218, 204, 250)",
  "Need Attention": "rgb(241, 221, 197)",
  "At Risk": "rgb(247, 211, 214)",
  Lost: "rgb(215, 221, 229)",
};
const SEGMENT_EXPLANATIONS = {
  Champions: "Highest-value customers with strong recency, frequency, and spend.",
  "Loyal Customers": "Very recent customers with healthy repeat behavior.",
  Promising: "Very recent customers with at least moderate frequency or spend.",
  "Need Attention": "Mid-recency customers who can move up or decline based on engagement.",
  "At Risk": "Previously valuable customers showing churn risk.",
  Lost: "Low-activity or lapsed customers with weak recency and engagement.",
};

function getSegmentColor(segment) {
  return SEGMENT_COLORS[segment] || "#64748b";
}

function getSegmentTint(segment, alpha = "3d") {
  if (alpha === "3d" && SEGMENT_TINT_COLORS[segment]) {
    return SEGMENT_TINT_COLORS[segment];
  }
  return `${getSegmentColor(segment)}${alpha}`;
}

function formatWholeNumber(value) {
  return formatNumber(Math.round(Number(value || 0)));
}

function MetricIcon({ iconKey }) {
  const key = String(iconKey || "users").toLowerCase();
  const icons = {
    users: (
      <>
        <path d="M8.8 11.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z" />
        <path d="M2.8 19.2c.6-3.2 2.6-5 6-5s5.4 1.8 6 5" />
        <path d="M16.2 11.4a2.8 2.8 0 1 0-.8-5.5" />
        <path d="M15.8 14.2c2.8.2 4.5 1.8 5.1 4.7" />
      </>
    ),
    bag: (
      <>
        <path d="M6.4 8.2h11.2l1 11H5.4l1-11Z" />
        <path d="M9 8.2V6.9a3 3 0 0 1 6 0v1.3" />
      </>
    ),
    eye: (
      <>
        <path d="M2.8 12s3.1-5 9.2-5 9.2 5 9.2 5-3.1 5-9.2 5-9.2-5-9.2-5Z" />
        <path d="M12 14.8a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6Z" />
      </>
    ),
    truck: (
      <>
        <path d="M3 6.5h10.5v9H3v-9Z" />
        <path d="M13.5 9h3.3l3.2 3.4v3.1h-6.5V9Z" />
        <path d="M6.7 18.4a1.9 1.9 0 1 0 0-3.8 1.9 1.9 0 0 0 0 3.8Z" />
        <path d="M17.1 18.4a1.9 1.9 0 1 0 0-3.8 1.9 1.9 0 0 0 0 3.8Z" />
      </>
    ),
    receipt: (
      <>
        <path d="M7 3.8h10v16.4l-2-1.2-2 1.2-2-1.2-2 1.2-2-1.2V3.8Z" />
        <path d="M9.6 8h4.8" />
        <path d="M9.6 12h4.8" />
        <path d="M9.6 15.4h2.8" />
      </>
    ),
    route: (
      <>
        <path d="M6.4 6.8a2.6 2.6 0 1 0 0-.1Z" />
        <path d="M17.6 17.2a2.6 2.6 0 1 0 0-.1Z" />
        <path d="M8.6 7.4h4.2c2.8 0 4.1 1 4.1 2.8s-1.3 2.8-4.1 2.8h-1.7c-2.7 0-4 1-4 3" />
      </>
    ),
    repeat: (
      <>
        <path d="M17.2 7.2H8.4a4.4 4.4 0 0 0-4.4 4.4v.4" />
        <path d="m14.8 4.8 2.4 2.4-2.4 2.4" />
        <path d="M6.8 16.8h8.8a4.4 4.4 0 0 0 4.4-4.4V12" />
        <path d="m9.2 19.2-2.4-2.4 2.4-2.4" />
      </>
    ),
    rupee: (
      <>
        <path d="M7 5h10" />
        <path d="M7 8.8h10" />
        <path d="M8 5c5.8 0 5.8 7.2 0 7.2l6.2 6.8" />
      </>
    ),
  };

  return (
    <span className="metric-command-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        {icons[key] || icons.users}
      </svg>
    </span>
  );
}

function getMetricDefinitions(terminology) {
  const customerPlural = terminology.customer_plural_label || "Customers";
  const revenueLabel = terminology.revenue_label || "Revenue";
  const transactionLabel = terminology.transaction_label || "Transaction";
  const activityLabel = terminology.activity_label || "Activity";
  const activityPlural = terminology.activity_plural_label || `${activityLabel}s`;
  const avgActivityFrequencyLabel =
    terminology.avg_activity_frequency_label || `Avg ${activityLabel} Frequency`;

  return {
    clients: {
      label: terminology.metric_clients_label || `Total ${customerPlural}`,
      shortLabel: terminology.metric_clients_short_label || customerPlural,
      summary: `${customerPlural} distribution across segments`,
      iconKey: terminology.metric_clients_icon || "users",
      formatter: formatNumber,
    },
    revenue: {
      label: terminology.metric_revenue_label || `Total ${revenueLabel}`,
      shortLabel: terminology.metric_revenue_short_label || revenueLabel,
      summary: `${revenueLabel} contribution across segments`,
      iconKey: terminology.metric_revenue_icon || "rupee",
      formatter: formatCurrency,
      compactFormatter: formatCompactCurrency,
    },
    avgTripValue: {
      label: terminology.metric_value_label || `Avg ${transactionLabel} Value`,
      shortLabel: terminology.metric_value_short_label || `Avg ${transactionLabel}`,
      summary:
        terminology.metric_value_summary ||
        `Average value per ${transactionLabel.toLowerCase()} by segment`,
      iconKey: terminology.metric_value_icon || "receipt",
      formatter: formatCurrency,
      compactFormatter: formatCompactCurrency,
    },
    avgTripFrequency: {
      label: terminology.metric_frequency_label || avgActivityFrequencyLabel,
      shortLabel: terminology.metric_frequency_short_label || `Avg ${activityPlural}`,
      summary: `Average ${activityPlural.toLowerCase()} per ${String(
        terminology.customer_label || "customer"
      ).toLowerCase()} by segment`,
      iconKey: terminology.metric_frequency_icon || "repeat",
      formatter: formatWholeNumber,
    },
  };
}

function getSegmentRows(segments, customers) {
  const segmentMap = new Map(
    (segments || []).map((item) => [item.segment, item])
  );

  return SEGMENT_ORDER.map((segment) => {
    const summary = segmentMap.get(segment) || {};
    const segmentCustomers = (customers || []).filter((customer) => customer.segment === segment);
    const customerCount = Number(summary.customer_count ?? segmentCustomers.length ?? 0);
    const revenue = Number(summary.revenue || 0);
    const visits = segmentCustomers.reduce(
      (sum, customer) => sum + Number(customer.visit_count || 0),
      0
    );

    return {
      segment,
      customerCount,
      revenue,
      visits,
      avgTripValue: visits > 0 ? revenue / visits : 0,
      avgTripFrequency: customerCount > 0 ? visits / customerCount : 0,
      revenueSharePct: Number(summary.revenue_share_pct || 0),
      customerSharePct: Number(summary.customer_share_pct || 0),
    };
  });
}

export default function KpiGrid({
  kpis,
  segmentSummary = { segments: [] },
  customers = [],
  terminology = {},
  selectedMetric: controlledSelectedMetric,
  onSelectedMetricChange,
  onTakeAction,
}) {
  const [internalSelectedMetric, setInternalSelectedMetric] = useState("clients");
  const [selectedSegment, setSelectedSegment] = useState("Champions");
  const [treemapSelectionOverlay, setTreemapSelectionOverlay] = useState(null);
  const [treemapChartHeight, setTreemapChartHeight] = useState(DEFAULT_TREEMAP_CHART_HEIGHT);
  const segmentCardRefs = useRef(new Map());
  const chartShellRef = useRef(null);
  const selectedMetric = controlledSelectedMetric || internalSelectedMetric;
  const setSelectedMetric = (nextMetric) => {
    setTreemapSelectionOverlay(null);
    setInternalSelectedMetric(nextMetric);
    onSelectedMetricChange?.(nextMetric);
    window.requestAnimationFrame(() => {
      const shell = chartShellRef.current;
      if (!shell) {
        return;
      }
      shell.querySelectorAll(".apexcharts-treemap-rect").forEach((node) => {
        node.classList.remove("apexcharts-keyboard-focused");
      });
    });
  };
  const metrics = useMemo(() => getMetricDefinitions(terminology), [terminology]);
  const metric = metrics[selectedMetric] || metrics.revenue;

  const segmentRows = useMemo(
    () => getSegmentRows(segmentSummary?.segments || [], customers),
    [customers, segmentSummary?.segments]
  );

  const metricTotals = useMemo(() => {
    const totalClients = Number(kpis?.total_customers || 0);
    const totalRevenue = Number(kpis?.total_revenue || 0);
    const totalVisits = (customers || []).reduce(
      (sum, customer) => sum + Number(customer.visit_count || 0),
      0
    );

    return {
      clients: totalClients,
      revenue: totalRevenue,
      avgTripValue: Number(kpis?.average_order_value || 0),
      avgTripFrequency: Number(kpis?.average_purchase_frequency || 0),
      totalVisits,
    };
  }, [customers, kpis]);

  const metricRows = useMemo(() => {
    return segmentRows
      .map((row) => {
        const value =
          selectedMetric === "clients"
            ? row.customerCount
            : selectedMetric === "revenue"
            ? row.revenue
            : selectedMetric === "avgTripValue"
            ? row.avgTripValue
            : row.avgTripFrequency;

        return { ...row, value };
      })
      .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
  }, [selectedMetric, segmentRows]);

  const activeSegment = useMemo(() => {
    if (selectedSegment && metricRows.some((row) => row.segment === selectedSegment)) {
      return selectedSegment;
    }
    return "";
  }, [metricRows, selectedSegment]);
  const clearTreemapSelectedRects = useCallback(() => {
    const shell = chartShellRef.current;
    if (!shell) {
      return;
    }

    shell.querySelectorAll(".apexcharts-treemap-rect").forEach((node) => {
      node.classList.remove("apexcharts-keyboard-focused");
      node.style.strokeDasharray = "0";
      if (typeof node.blur === "function") {
        node.blur();
      }
    });
  }, []);
  const clearTreemapFocusSoon = useCallback(() => {
    [0, 40, 120, 260].forEach((delay) => {
      window.setTimeout(clearTreemapSelectedRects, delay);
    });
  }, [clearTreemapSelectedRects]);

  const updateTreemapSelectionOverlay = useCallback((segmentName) => {
    const shell = chartShellRef.current;
    if (!shell || !segmentName) {
      setTreemapSelectionOverlay(null);
      return;
    }

    const targetIndex = metricRows.findIndex((row) => row.segment === segmentName);
    const targetNode = targetIndex >= 0
      ? shell.querySelector(`.apexcharts-treemap-rect[j="${targetIndex}"]`)
      : null;

    if (!targetNode) {
      setTreemapSelectionOverlay(null);
      return;
    }

    targetNode.classList.remove("apexcharts-keyboard-focused");
    targetNode.style.strokeDasharray = "0";

    const shellRect = shell.getBoundingClientRect();
    const nodeRect = targetNode.getBoundingClientRect();
    setTreemapSelectionOverlay({
      left: nodeRect.left - shellRect.left,
      top: nodeRect.top - shellRect.top,
      width: nodeRect.width,
      height: nodeRect.height,
      color: getSegmentColor(segmentName),
    });
  }, [metricRows]);
  const chartData = useMemo(() => {
    const rawData = metricRows.map((row) => {
      const value = Number(row.value || 0);

      return {
        x: getSegmentDisplayName(row.segment),
        y: value,
        segment: row.segment,
        value,
        visits: row.visits,
        customers: row.customerCount,
        revenue: row.revenue,
        fillColor: getSegmentTint(row.segment),
      };
    });

    const visibleTotal = rawData.reduce((sum, item) => sum + Number(item.value || 0), 0);
    const dataWithShare = rawData.map((item) => ({
      ...item,
      sharePct: visibleTotal > 0 ? (Number(item.value || 0) / visibleTotal) * 100 : 0,
    }));

    return {
      series: [{ name: metric.label, data: dataWithShare }],
      total: metricTotals[selectedMetric] || 0,
      visibleTotal,
      topSegment: dataWithShare.reduce(
        (top, item) => (Number(item.value || 0) > Number(top.value || 0) ? item : top),
        dataWithShare[0] || { x: "", value: 0 }
      ),
    };
	  }, [metric.label, metricRows, metricTotals, selectedMetric]);

  const rankedRows = useMemo(() => {
    const maxValue = Math.max(...metricRows.map((row) => Number(row.value || 0)), 0);

    return segmentRows.map((row) => {
      const value =
        selectedMetric === "clients"
          ? row.customerCount
          : selectedMetric === "revenue"
          ? row.revenue
          : selectedMetric === "avgTripValue"
          ? row.avgTripValue
          : row.avgTripFrequency;

      return {
        ...row,
        value,
        barPct: maxValue > 0 ? Math.max((Number(value || 0) / maxValue) * 100, 3) : 3,
      };
    });
  }, [metricRows, segmentRows, selectedMetric]);
  const updateSelectedSegment = useCallback((nextSegment) => {
    setSelectedSegment(nextSegment);
  }, []);

  useEffect(() => {
    const shell = chartShellRef.current;
    if (!shell || typeof window === "undefined") {
      return undefined;
    }

    const measureChartHeight = () => {
      const styles = window.getComputedStyle(shell);
      const verticalPadding =
        Number.parseFloat(styles.paddingTop || "0") + Number.parseFloat(styles.paddingBottom || "0");
      const nextHeight = Math.max(320, Math.floor(shell.clientHeight - verticalPadding));
      setTreemapChartHeight((currentHeight) =>
        Math.abs(currentHeight - nextHeight) > 1 ? nextHeight : currentHeight
      );
    };

    measureChartHeight();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measureChartHeight);
      observer.observe(shell);
      window.addEventListener("resize", measureChartHeight);
      return () => {
        observer.disconnect();
        window.removeEventListener("resize", measureChartHeight);
      };
    }

    window.addEventListener("resize", measureChartHeight);
    return () => window.removeEventListener("resize", measureChartHeight);
  }, []);

  useEffect(() => {
    if (!activeSegment) {
      clearTreemapSelectedRects();
      setTreemapSelectionOverlay(null);
      return undefined;
    }

    const scrollFrame = window.requestAnimationFrame(() => {
      const activeCard = segmentCardRefs.current.get(activeSegment);
      clearTreemapSelectedRects();
      updateTreemapSelectionOverlay(activeSegment);
      activeCard?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });

    return () => window.cancelAnimationFrame(scrollFrame);
  }, [activeSegment, clearTreemapSelectedRects, updateTreemapSelectionOverlay]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      clearTreemapSelectedRects();
      if (activeSegment) {
        updateTreemapSelectionOverlay(activeSegment);
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeSegment, chartData.series, clearTreemapSelectedRects, treemapChartHeight, updateTreemapSelectionOverlay]);

  useEffect(() => {
    if (!activeSegment) {
      return undefined;
    }

    const onResize = () => {
      clearTreemapSelectedRects();
      updateTreemapSelectionOverlay(activeSegment);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [activeSegment, clearTreemapSelectedRects, updateTreemapSelectionOverlay]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "treemap",
        background: "transparent",
        toolbar: { show: false },
        animations: {
          enabled: false,
          animateGradually: { enabled: false },
          dynamicAnimation: { enabled: false },
        },
        fontFamily: APP_FONT_FAMILY,
        foreColor: "#163466",
        events: {
          dataPointSelection: (_event, _chartContext, config) => {
            const point = config?.w?.config?.series?.[0]?.data?.[config.dataPointIndex];
            if (point?.segment) {
              updateSelectedSegment(point.segment);
              window.requestAnimationFrame(() => {
                clearTreemapFocusSoon();
                updateTreemapSelectionOverlay(point.segment);
              });
            }
          },
        },
      },
      legend: { show: false },
      colors: metricRows.map((row) => getSegmentTint(row.segment)),
      dataLabels: {
        enabled: true,
        formatter: (text, opts) => {
          const point = opts?.w?.config?.series?.[0]?.data?.[opts.dataPointIndex] || {};
          const share = Number(point.sharePct || 0);
          const label = `${text} (${share.toFixed(1)}%)`;
          const value = metric.formatter(point.value || 0);
          if (share > 0 && share < MIN_TREEMAP_LABEL_SHARE) {
            return "";
          }

          return [label, value];
        },
        style: {
          fontSize: "16px",
          fontWeight: 900,
          colors: ["#10224f"],
        },
        dropShadow: {
          enabled: false,
        },
      },
      plotOptions: {
        treemap: {
          distributed: true,
          enableShades: false,
          borderRadius: 7,
          dataLabels: {
            format: "scale",
          },
        },
      },
      stroke: {
        show: true,
        width: 4,
        colors: ["rgba(255,255,255,0.96)"],
      },
      states: {
        hover: {
          filter: {
            type: "none",
          },
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: "none",
          },
        },
      },
      tooltip: {
        enabled: true,
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          const point = w.config.series[seriesIndex].data[dataPointIndex] || {};
          const value = Number(point.value || 0);
          const meta = `${Number(point.sharePct || 0).toFixed(1)}% of visible segments`;

          return `
            <div class="metric-treemap-tooltip">
              <div class="metric-tooltip-segment">${getSegmentDisplayName(point.segment || point.x) || ""}</div>
              <div class="metric-tooltip-value">${metric.formatter(value)}</div>
              <div class="metric-tooltip-meta">${meta}</div>
              <div class="metric-tooltip-foot">${formatNumber(point.customers || 0)} clients · ${formatNumber(point.visits || 0)} visits</div>
            </div>
          `;
        },
      },
    }),
    [clearTreemapFocusSoon, metric, metricRows, updateSelectedSegment, updateTreemapSelectionOverlay]
  );

  const activeMetricKeys = Object.keys(metrics);
  const getMetricButtonValue = (key) => {
    const value =
      key === "clients"
        ? metricTotals.clients
        : key === "revenue"
        ? metricTotals.revenue
        : key === "avgTripValue"
        ? metricTotals.avgTripValue
        : metricTotals.avgTripFrequency;

    return (metrics[key] || metric).formatter(value || 0);
  };
  const getSegmentSummaryForRow = (row) =>
    (segmentSummary?.segments || []).find((item) => item.segment === row.segment) || {};
  const getSegmentExplanationForRow = (row) => {
    const summary = getSegmentSummaryForRow(row);
    return SEGMENT_EXPLANATIONS[row.segment] || summary.segment_description || "Customer segment";
  };
  const getSegmentInsightForRow = (row) => {
    const summary = getSegmentSummaryForRow(row);
    const matchedInsight = (segmentSummary?.insights || []).find((insight) =>
      String(insight || "").toLowerCase().includes(String(row.segment || "").toLowerCase())
    );
    if (matchedInsight) {
      return matchedInsight;
    }

    const fallbackInsight = summary.segment_description || "";
    const explanation = getSegmentExplanationForRow(row);
    return fallbackInsight && fallbackInsight !== explanation ? fallbackInsight : "";
  };
  const getActionPayloadForRow = (row) => ({
    ...getSegmentSummaryForRow(row),
    segment: row.segment,
    customer_count: row.customerCount,
    customer_share_pct: row.customerSharePct,
    revenue: row.revenue,
    revenue_share_pct: row.revenueSharePct,
  });
  return (
    <section className="card metric-explorer-card">
      <div className="metric-explorer-head">
        <div>
          <div className="card-label">Segment Metric Explorer</div>
          <div className="metric-explorer-title">{metric.label}</div>
          <div className="metric-explorer-subtitle">{metric.summary}</div>
        </div>

      </div>

      <div className="metric-command-row" role="tablist" aria-label="Segment metric">
        {activeMetricKeys.map((key) => {
          const item = metrics[key];
          const isActive = key === selectedMetric;
          return (
            <button
              key={key}
              type="button"
              className={`metric-command-btn${isActive ? " active" : ""}`}
              onClick={() => setSelectedMetric(key)}
              aria-selected={isActive}
              role="tab"
            >
              <MetricIcon iconKey={item.iconKey} />
              <span>{item.shortLabel}</span>
              <strong>{getMetricButtonValue(key)}</strong>
            </button>
          );
        })}
      </div>

      <div className="metric-explorer-body">
        <div
          className="metric-chart-shell"
          ref={chartShellRef}
          style={{
            "--active-segment-color": activeSegment ? getSegmentColor(activeSegment) : "#1a6ef5",
          }}
        >
          <Chart
            options={chartOptions}
            series={chartData.series}
            type="treemap"
            height={treemapChartHeight}
          />
          {treemapSelectionOverlay ? (
            <span
              className="metric-treemap-selection-overlay"
              style={{
                left: treemapSelectionOverlay.left,
                top: treemapSelectionOverlay.top,
                width: treemapSelectionOverlay.width,
                height: treemapSelectionOverlay.height,
                "--selection-color": treemapSelectionOverlay.color,
              }}
              aria-hidden="true"
            />
          ) : null}
        </div>

        <aside className="metric-side-panel metric-segment-panel">
          <div
            className={`metric-segment-card-grid${activeSegment ? " has-active" : ""}`}
            aria-label="Segment overview cards"
          >
            {rankedRows.map((row) => {
              const isActiveSegment = row.segment === activeSegment;
              const segmentColor = getSegmentColor(row.segment);
              const segmentDisplayName = getSegmentDisplayName(row.segment);
              const segmentExplanation = getSegmentExplanationForRow(row);
              const segmentInsight = getSegmentInsightForRow(row);
              return (
                <article
                  className={`metric-segment-mini-card${isActiveSegment ? " active expanded" : ""}`}
                  key={row.segment}
                  ref={(node) => {
                    if (node) {
                      segmentCardRefs.current.set(row.segment, node);
                    } else {
                      segmentCardRefs.current.delete(row.segment);
                    }
                  }}
                  style={{
                    "--segment-color": segmentColor,
                    "--segment-tint": getSegmentTint(row.segment),
                    background: `${segmentColor}12`,
                    borderColor: `${segmentColor}4d`,
                  }}
                >
                  <div
                    className="metric-segment-summary-head"
                    style={{ color: segmentColor }}
                  >
                    <button
                      type="button"
                      className="metric-segment-toggle"
                      onClick={() => updateSelectedSegment(isActiveSegment ? "" : row.segment)}
                      aria-expanded={isActiveSegment}
                      style={{ color: segmentColor }}
                    >
                      <span className="metric-segment-title-block">
                        <span className="metric-segment-title-wrap">
                          <span>{segmentDisplayName}</span>
                          <SegmentCrest color={segmentColor} segmentName={row.segment} />
                          <span className="metric-segment-value-box">
                            <strong className="metric-segment-header-value">{metric.formatter(row.value || 0)}</strong>
                          </span>
                        </span>
	                      {isActiveSegment && onTakeAction ? (
                          <span className="metric-segment-inline-description">{segmentExplanation}</span>
                        ) : null}
                      </span>
                    </button>
                    <span className="metric-segment-head-actions">
                      {isActiveSegment ? (
                        <button
                          type="button"
                          className={`segment-action-btn metric-segment-action-pill${
                            !Number(row.customerCount || 0) ? " disabled" : ""
                          }`}
                          disabled={!Number(row.customerCount || 0)}
                          onClick={() => onTakeAction?.(getActionPayloadForRow(row))}
                        >
                          Suggestive Action
                        </button>
                      ) : null}
                    </span>
                  </div>

                  {isActiveSegment ? (
                    <div className="metric-segment-open-body">
                      {segmentInsight ? (
                        <div className="segment-insight metric-segment-overview-insight">
                          {renderHighlightedInsight(segmentInsight)}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </aside>
      </div>
    </section>
  );
}
